import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import { VoiceIntakeSettings, VoiceIntakePatch } from '../models/VoiceAgent'
import { useUser } from './useUser'

const VOICE_AGENT_KEY = 'voice-agent'
const BASE = '/voice-agent'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        if (typeof data.error === 'string' && data.error) {
            return data.error
        }
    }

    return fallback
}

export const useVoiceAgentIntake = () => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: intakeSettings, isLoading, error, refetch } = useQuery<
        VoiceIntakeSettings,
        AxiosError
    >({
        queryKey: [VOICE_AGENT_KEY, 'intake', apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<VoiceIntakeSettings>(
                `${BASE}/setup/intake/`,
            )
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnWindowFocus: false,
    })

    const saveIntake = useMutation<
        { status: string },
        AxiosError,
        VoiceIntakePatch
    >({
        mutationFn: async (body) => {
            const res = await axiosInstance.patch<{ status: string }>(
                `${BASE}/setup/intake/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [VOICE_AGENT_KEY],
            })
            toast.success('Intake settings saved')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to save intake settings'))
        },
    })

    return {
        intakeSettings,
        isLoading,
        error,
        refetch,
        saveIntake,
    }
}
